/**
 * Client-side image resizer — compresses photos before upload.
 *
 * Uses createImageBitmap (off-main-thread decoding in supporting browsers)
 * and OffscreenCanvas/HTMLCanvasElement for the resize.
 *
 * Typical reduction: 6MB iPhone photo → ~200-400KB JPEG.
 */

const MAX_DIMENSION = 1920; // Max width or height in pixels
const JPEG_QUALITY = 0.82; // Balance between quality and file size
const SKIP_THRESHOLD = 500 * 1024; // Don't resize if already under 500KB

export async function resizeImage(file: File): Promise<File> {
	// Skip non-image files (e.g., PDFs)
	if (!file.type.startsWith('image/')) {
		return file;
	}

	// Skip small files — they don't need resizing
	if (file.size <= SKIP_THRESHOLD) {
		return file;
	}

	try {
		// Decode image off the main thread (where supported)
		const bitmap = await createImageBitmap(file);

		const { width, height } = bitmap;

		// Calculate new dimensions maintaining aspect ratio
		let newWidth = width;
		let newHeight = height;

		if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
			if (width > height) {
				newWidth = MAX_DIMENSION;
				newHeight = Math.round((height / width) * MAX_DIMENSION);
			} else {
				newHeight = MAX_DIMENSION;
				newWidth = Math.round((width / height) * MAX_DIMENSION);
			}
		} else if (file.size <= 1024 * 1024) {
			// Image is small enough in dimensions AND file size — skip
			bitmap.close();

			return file;
		}

		// Use OffscreenCanvas if available (fully off-main-thread), else fallback
		let blob: Blob;

		if (typeof OffscreenCanvas !== 'undefined') {
			const canvas = new OffscreenCanvas(newWidth, newHeight);
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				bitmap.close();

				return file;
			}
			ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
			blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY });
		} else {
			// Fallback for iOS Safari (no OffscreenCanvas support)
			const canvas = document.createElement('canvas');
			canvas.width = newWidth;
			canvas.height = newHeight;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				bitmap.close();

				return file;
			}
			ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
			blob = await new Promise<Blob>((resolve) => {
				canvas.toBlob(
					(b) => resolve(b || new Blob()),
					'image/jpeg',
					JPEG_QUALITY,
				);
			});
		}

		bitmap.close();

		// Create a new File with a cleaned filename
		const resizedName = file.name.replace(/\.[^.]+$/, '.jpg');

		return new File([blob], resizedName, {
			type: 'image/jpeg',
			lastModified: Date.now(),
		});
	} catch {
		// If anything fails, return the original file — graceful fallback
		return file;
	}
}

/**
 * Resize multiple images, yielding to the main thread between each
 * to prevent UI jank.
 */
export async function resizeImages(files: File[]): Promise<File[]> {
	const results: File[] = [];

	for (const file of files) {
		const resized = await resizeImage(file);
		results.push(resized);

		// Yield to the main thread between images to keep UI responsive
		await new Promise((resolve) => setTimeout(resolve, 0));
	}

	return results;
}
